import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsArticles, newsCategories } from "@/mocks/news";
import { formatDate, formatViews } from "@/utils/format";

export default function NewsCategoryPage() {
  usePageMeta({
    title: "资讯分类 - 成人网",
    description:
      "成人网资讯分类页，涵盖行业报道、资讯动态、健康知识与文化观察，提供隐私保护、内容分级与夜间用眼等话题的深度阅读。",
    keywords: "成人资讯,行业报道,健康知识,内容分级,成人网",
  });

  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      newsArticles.filter((article) => category === "全部" || article.category === category),
    [category],
  );

  const perPage = 4;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const items = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className="container-page py-7">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯分类" }]} />

      <div className="mb-7 border-b border-background-200 pb-6">
        <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">Newsroom</span>
        <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-[32px]">资讯分类</h1>
        <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-foreground-500">
          关注行业变化、内容治理与观看健康。这里的文章偏向观察与实用建议，
          不做夸张的标题，也不追逐热搜。
        </p>
      </div>

      <div className="mb-7 flex flex-wrap items-center gap-2">
        {newsCategories.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => {
              setCategory(item);
              setPage(1);
            }}
            className={`rounded-full px-3.5 py-1.5 text-[11.5px] transition-colors cursor-pointer ${
              category === item
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-500 hover:text-foreground-900"
            }`}
          >
            {item}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-foreground-500">共 {filtered.length} 篇</span>
      </div>

      {items.length > 0 ? (
        <>
          <div className="space-y-3">
            {items.map((article) => (
              <Link
                key={article.id}
                to={`/news-detail/${article.id}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-700 sm:flex-row"
              >
                <div className="h-[168px] w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[132px] sm:w-[200px]">
                  <img
                    src={article.cover}
                    alt={`${article.title} - 成人资讯配图`}
                    title={`${article.title} 成人资讯`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="rounded-sm bg-accent-500/15 px-1.5 py-0.5 text-accent-500">
                      {article.category}
                    </span>
                    <span className="text-foreground-500">{formatDate(article.publishDate)}</span>
                    <span className="text-foreground-500">·</span>
                    <span className="text-foreground-500">{article.author}</span>
                  </div>
                  <h2 className="mt-2 text-base font-medium leading-relaxed text-foreground-950 transition-colors group-hover:text-accent-500">
                    {article.title}
                  </h2>
                  <p className="mt-1.5 line-clamp-2-safe text-xs leading-relaxed text-foreground-500">
                    {article.excerpt}
                  </p>
                  <div className="mt-3 flex items-center gap-3 text-[11px] text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(article.views)}
                    </span>
                    <span className="flex items-center gap-1 text-accent-600">
                      阅读全文
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon="ri-newspaper-line"
          title="暂无该主题的资讯"
          description="这个分类下还没有发布文章，先看看全部资讯，或者换个主题浏览。"
          actionLabel="查看全部资讯"
          onAction={() => {
            setCategory("全部");
            setPage(1);
          }}
        />
      )}
    </div>
  );
}